import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type LegacyButtonType =
  | "default"
  | "primary"
  | "ghost"
  | "dashed"
  | "link"
  | "text";

interface BaseModalProps {
  title: string;
  description?: string;
  titleIcon?: JSX.Element;
  destroyOnClose?: boolean;
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  onOk?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onCancel?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  footer?: React.ReactNode | null;
  okText?: React.ReactNode;
  okType?: LegacyButtonType;
  cancelText?: React.ReactNode;
  okButtonLoadingIndicator?: boolean;
  okButtonDisabled?: boolean;
  okButtonIcon?: JSX.Element;
  cancelButtonIcon?: JSX.Element;
  children?: React.ReactNode;
  dialogContentClassName?: string;
  dialogBodyClassName?: string;
  noPadding?: boolean;
  hideCloseButton?: boolean;
}

export function BaseModal({
  title,
  titleIcon,
  description,
  open,
  onOpenChange,
  onOk,
  onCancel,
  footer,
  okText = "OK",
  okType = "primary",
  cancelText = "Cancel",
  okButtonLoadingIndicator = false,
  okButtonDisabled = false,
  okButtonIcon,
  cancelButtonIcon,
  children,
  dialogContentClassName,
  dialogBodyClassName,
  noPadding = false,
  hideCloseButton = false,
}: BaseModalProps) {
  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!okButtonLoadingIndicator) {
      onCancel?.(e);
      onOpenChange?.(false);
    }
  };

  const handleOk = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!okButtonLoadingIndicator) {
      onOk?.(e);
    }
  };

  const getButtonVariant = (type: LegacyButtonType) => {
    switch (type) {
      case "primary":
        return "default";
      case "ghost":
        return "ghost";
      case "link":
        return "link";
      default:
        return "outline";
    }
  };

  const defaultFooter = (
    <div className="flex justify-end space-x-2">
      <Button
        variant="outline"
        onClick={handleCancel}
        disabled={okButtonLoadingIndicator}
      >
        {cancelButtonIcon && <span className="mr-2">{cancelButtonIcon}</span>}
        {cancelText}
      </Button>
      <Button
        variant={getButtonVariant(okType)}
        onClick={handleOk}
        disabled={okButtonDisabled || okButtonLoadingIndicator}
      >
        {okButtonLoadingIndicator ? (
          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : okButtonIcon ? (
          <span className="mr-2">{okButtonIcon}</span>
        ) : null}
        {okText}
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "max-w-2xl max-h-[90vh] p-0 flex flex-col gap-0",
          dialogContentClassName
        )}
        showCloseButton={!hideCloseButton}
        onInteractOutside={(e) => {
          // Prevent closing when clicking outside the modal
          e.preventDefault();
        }}
      >
        <DialogHeader className="sticky top-0 bg-background rounded-lg p-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            {titleIcon}
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div
          className={cn(
            "flex-1 min-h-0 overflow-auto",
            {
              "p-4": !noPadding,
            },
            dialogBodyClassName
          )}
        >
          {children}
        </div>
        {footer !== null && (
          <div className="bg-background z-10 p-4 border-t rounded-lg">
            {footer ?? defaultFooter}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
